/**
 * TaxLossPage — Tax-loss harvesting scanner
 *
 * Finds positions with unrealised losses in taxable wrappers (GIA / Crypto)
 * that can be crystallised to offset gains or create a carry-forward loss.
 */
import { useEffect, useState } from 'react'
import { Scissors, RefreshCw, TrendingDown, AlertCircle, CalendarClock, CheckCircle2 } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import TaxWorkspaceTabs from '@/components/ui/TaxWorkspaceTabs'

interface WashSaleRisk {
  risk: 'high' | 'medium' | 'none'
  last_buy_date: string | null
  days_since_buy: number | null
  recent_buy_count: number
  note: string
}

// NOTE: field names MUST match backend/api/tax_loss_harvesting.py exactly.
// A silent drift here (unrealised_loss_gbp vs unrealised_pnl_gbp, top-level
// tax_year vs cgt_summary.tax_year) is what broke this page before — the
// table crashed on `undefined.toFixed` whenever real candidates existed.
interface Candidate {
  holding_id: number
  name: string
  symbol: string | null
  account_id: number
  account_name: string | null
  wrapper: string | null
  owner?: string | null
  quantity: number | null
  avg_cost: number | null
  cost_basis_gbp: number | null
  current_value_gbp: number | null
  unrealised_pnl_gbp: number | null
  pct_change: number | null
  est_tax_saving_gbp: number | null
  bed_and_isa_suitable: boolean | null
  bed_and_spouse_suitable: boolean | null
  notes: string | null
  wash_sale_risk?: WashSaleRisk | null
}

interface Recommendation {
  priority: 'HIGH' | 'MODERATE' | 'LOW'
  title: string
  detail: string
}

interface CgtSummary {
  tax_year: string | null
  cgt_allowance_gbp: number | null
  allowance_used_gbp: number | null
  allowance_remaining_gbp: number | null
  total_unrealised_gain_gbp: number | null
  total_harvestable_loss_gbp: number | null
  net_gains_after_harvest_gbp: number | null
  taxable_amount_gbp: number | null
  estimated_cgt_liability_gbp: number | null
  potential_tax_saving_gbp: number | null
  tax_saving_detail: string | null
}

interface ScanResponse {
  headline: string
  cgt_summary: CgtSummary | null
  marginal_rate_pct: number | null
  taxable_holding_count?: number | null
  empty_state_reason?: 'all_sheltered' | 'taxable_all_in_profit' | null
  total_harvestable_loss_gbp: number | null
  total_unrealised_gain_gbp: number | null
  total_unrealised_loss_gbp: number | null
  total_tax_savings_gbp: number | null
  tax_free_losses_gbp: number | null
  candidates: Candidate[]
  candidates_with_wash_risk?: number | null
  recommendations: Recommendation[]
  notes: string[]
}

// ── Year-end tax actions types ───────────────────────────────────────────────

interface YearEndAction {
  key: string
  title: string
  detail: string
  est_benefit_gbp: number | null
  benefit_type: 'tax_saved' | 'allowance' | 'shelter'
  priority: string
  available: boolean
}

interface YearEndActionsResponse {
  actions: YearEndAction[]
  total_quantified_benefit_gbp: number
  tax_year_end: string
  days_to_year_end: number
  wash_sale_excluded_count: number
  note: string
}

// ── Formatters ───────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined) => {
  const v = n ?? 0
  return `${v < 0 ? '−' : ''}£${Math.abs(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

/** Compact badge showing acquisition-based wash-sale risk for a candidate.
 *  Shown in the candidate table row. Mobile-visible (inline, wraps with other badges).
 *  The full advisory note is surfaced as a tooltip (title attribute). */
function WashSaleRiskBadge({ wsr }: { wsr: WashSaleRisk | null | undefined }) {
  if (wsr == null || wsr.risk === 'none') return null
  if (wsr.risk === 'high') {
    return (
      <span
        className="badge badge-red text-[9px] cursor-help"
        title={wsr.note ?? 'Wash-sale risk: recent buy detected within 30-day window'}
        aria-label="Wash-sale risk: recent buy within 30-day window"
      >
        Wash-sale risk
      </span>
    )
  }
  // medium
  return (
    <span
      className="badge badge-amber text-[9px] cursor-help"
      title={wsr.note ?? 'Near 30-day window: recent buy close to the wash-sale boundary'}
      aria-label="Near 30-day window"
    >
      Near 30-day window
    </span>
  )
}

const PRIORITY_COLOURS = {
  HIGH: 'border-rose/30 bg-rose/5',
  MODERATE: 'border-amber/30 bg-amber/5',
  LOW: 'border-blue/30 bg-blue/5',
}

// ── Year-End Tax Actions Card ─────────────────────────────────────────────────

function YearEndActionsCard() {
  const [data, setData] = useState<YearEndActionsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .get<YearEndActionsResponse>('/api/tax-loss/year-end-actions')
      .then(res => {
        if (!cancelled) setData(res.data)
      })
      .catch(err => {
        if (!cancelled) setError(err?.message ?? 'Failed to load year-end actions')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="card p-5 space-y-3" aria-busy="true">
        <div className="h-5 w-48 skeleton" />
        <div className="h-3 w-full skeleton" />
        <div className="h-3 w-3/4 skeleton" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card p-4 border border-rose/20 text-xs text-rose-light">
        Could not load year-end actions: {error ?? 'no data'}
      </div>
    )
  }

  const { actions, total_quantified_benefit_gbp, tax_year_end, days_to_year_end, note } = data

  const urgencyColour =
    days_to_year_end <= 30
      ? 'text-rose-light'
      : days_to_year_end <= 90
        ? 'text-amber'
        : 'text-emerald-light'

  const BENEFIT_BADGE: Record<string, string> = {
    tax_saved: 'badge-emerald',
    allowance: 'badge-blue',
    shelter: 'badge-purple',
  }

  const BENEFIT_LABEL: Record<string, string> = {
    tax_saved: 'Tax saved',
    allowance: 'Use-it-or-lose-it',
    shelter: 'Shelter',
  }

  return (
    <section className="card p-5 space-y-4" aria-label="Year-end tax actions checklist">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-blue-light flex-shrink-0" aria-hidden />
          <div>
            <h2 className="panel-title">Year-End Tax Actions</h2>
            <p className="text-[11px] text-text-secondary">Do these before 5 April</p>
          </div>
        </div>
        <div className="text-right">
          <p className={clsx('text-lg font-bold tabular-nums', urgencyColour)}>
            {days_to_year_end}d
          </p>
          <p className="text-[10px] text-text-muted">to {tax_year_end}</p>
        </div>
      </div>

      {/* Headline benefit */}
      {total_quantified_benefit_gbp > 0 && (
        <div className="rounded-lg bg-emerald/5 border border-emerald/20 px-4 py-3">
          <p className="text-xs text-text-secondary">
            Quantified tax at stake
          </p>
          <p className="text-xl font-bold text-emerald-light tabular-nums">
            {fmt(total_quantified_benefit_gbp)}
          </p>
          <p className="text-[10px] text-text-muted mt-0.5">
            Sum of estimated £ benefit across ranked actions below
          </p>
        </div>
      )}

      {/* Actions list */}
      {actions.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-4">
          No year-end tax actions identified — everything looks used or unavailable.
        </p>
      ) : (
        <ol className="space-y-3">
          {actions.map((action, idx) => (
            <li
              key={action.key}
              className={clsx(
                'rounded-xl border p-4 transition-colors',
                !action.available
                  ? 'border-border/30 bg-surface-card/30 opacity-60'
                  : action.priority === 'HIGH'
                    ? 'border-emerald/20 bg-emerald/5'
                    : action.priority === 'MEDIUM'
                      ? 'border-amber/20 bg-amber/5'
                      : 'border-border bg-surface-card',
              )}
            >
              <div className="flex items-start gap-3">
                {/* Rank number */}
                <span
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-surface-card border border-border flex items-center justify-center text-[10px] font-bold text-text-muted mt-0.5"
                  aria-label={`Priority ${idx + 1}`}
                >
                  {idx + 1}
                </span>

                <div className="flex-1 min-w-0 space-y-1.5">
                  {/* Title row */}
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-text-primary leading-tight">
                      {action.title}
                    </p>
                    {/* Benefit badge */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {action.est_benefit_gbp != null ? (
                        <span className="text-sm font-bold text-emerald-light tabular-nums">
                          {fmt(action.est_benefit_gbp)}
                        </span>
                      ) : (
                        <span className="text-[10px] text-text-muted italic">qualitative</span>
                      )}
                      <span
                        className={clsx(
                          'badge text-[9px]',
                          BENEFIT_BADGE[action.benefit_type] ?? 'badge-blue',
                        )}
                      >
                        {BENEFIT_LABEL[action.benefit_type] ?? action.benefit_type}
                      </span>
                    </div>
                  </div>

                  {/* Detail */}
                  <p className="text-xs text-text-secondary leading-relaxed">{action.detail}</p>

                  {/* Unavailable marker */}
                  {!action.available && (
                    <p className="text-[10px] text-text-muted italic flex items-center gap-1">
                      <CheckCircle2 size={11} aria-hidden />
                      Not available / already used
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* Disclaimer */}
      <p className="text-[10px] text-text-muted leading-relaxed border-t border-border/30 pt-3">
        {note}
      </p>
    </section>
  )
}

export default function TaxLossPage() {
  const [data, setData] = useState<ScanResponse | null>(null)
  // 24% = higher-rate CGT on shares since 30 Oct 2024 (18% basic). The
  // household is comfortably higher-rate, so default to 24.
  const [marginalRate, setMarginalRate] = useState(24)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchScan = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<ScanResponse>('/api/tax-loss/scan', {
        params: { marginal_rate_pct: marginalRate },
      })
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load scan')
    } finally {
      setLoading(false)
    }
  }

  // Single effect: runs on mount AND (debounced) whenever the rate changes.
  useEffect(() => {
    const t = setTimeout(fetchScan, 100)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marginalRate])

  if (loading && !data) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="h-8 w-64 skeleton" />
        <div className="h-32 skeleton rounded-xl" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error ?? 'No data'}</p>
        <button type="button" onClick={fetchScan} className="btn-secondary mt-3">Retry</button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Scissors size={20} className="text-blue-light" aria-hidden />
            Tax-Loss Harvesting{data.cgt_summary?.tax_year ? ` · ${data.cgt_summary.tax_year}` : ''}
          </h1>
          <p className="text-sm text-text-secondary">
            Positions you could sell to realise losses and offset CGT
          </p>
        </div>
        <button type="button" onClick={fetchScan} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      <TaxWorkspaceTabs />

      {/* Headline — when the scan is empty it explains WHY (all-sheltered
          households read this as good news, not a broken feature) */}
      <div
        className={clsx(
          'card p-5 border-2',
          data.empty_state_reason === 'all_sheltered'
            ? 'border-emerald/30 bg-emerald/5'
            : 'border-blue/20 bg-blue/5',
        )}
      >
        <p className="text-sm text-text-primary">{data.headline}</p>
        {data.empty_state_reason === 'all_sheltered' && (
          <p className="text-xs text-text-muted mt-2">
            Tax-loss harvesting only applies to positions in taxable wrappers
            (GIA / Trading / Crypto). This page will light up if you ever hold
            investments outside an ISA or SIPP.
          </p>
        )}
      </div>

      {/* Scan-level wash-sale warning — shown when any candidate has a high-risk recent buy */}
      {(data.candidates_with_wash_risk ?? 0) > 0 && (
        <div
          className="card p-4 border border-rose/30 bg-rose/5 flex items-start gap-3"
          role="alert"
          aria-label="Wash-sale risk warning"
        >
          <AlertCircle size={16} className="text-rose-light flex-shrink-0 mt-0.5" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-text-primary">
              Wash-sale risk: {data.candidates_with_wash_risk} position{data.candidates_with_wash_risk !== 1 ? 's' : ''} with a recent buy
            </p>
            <p className="text-xs text-text-secondary mt-1">
              One or more candidates have a buy recorded within the last 30 days. Under the UK 30-day
              bed-&-breakfast rule (TCGA 1992 s106A), harvesting a loss now may be denied by HMRC if
              the disposal is matched to the recent acquisition. Review the{' '}
              <span className="text-rose-light font-medium">Wash-sale risk</span> badges below before
              acting. Consider bed-&-ISA or bed-&-spouse alternatives. Advisory only — not tax advice.
            </p>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
        <div className="stat-card">
          <p className="stat-label">Unrealised gains</p>
          <p className="stat-value text-emerald-light">+{fmt(data.total_unrealised_gain_gbp)}</p>
          <p className="text-xs text-text-muted">across all wrappers</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Unrealised losses</p>
          <p className="stat-value text-rose-light">{fmt(data.total_unrealised_loss_gbp)}</p>
          <p className="text-xs text-text-muted">
            of which {fmt(data.tax_free_losses_gbp)} tax-free (ISA/SIPP)
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Harvestable loss</p>
          <p className="stat-value text-amber">{fmt(data.total_harvestable_loss_gbp)}</p>
          <p className="text-xs text-text-muted">taxable wrappers only</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Est. tax saving</p>
          <p className="stat-hero text-blue-light">{fmt(data.total_tax_savings_gbp)}</p>
          <p className="text-xs text-text-muted">at {data.marginal_rate_pct ?? marginalRate}% CGT</p>
        </div>
      </div>

      {/* Marginal rate selector — 18/24 are the CGT rates on shares AND
          property since 30 Oct 2024 (the old 10/20 share rates are gone) */}
      <div className="card p-4">
        <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-2">
          Your CGT rate
        </label>
        <div className="flex gap-2">
          {[18, 24].map(rate => (
            <button
              key={rate}
              type="button"
              onClick={() => setMarginalRate(rate)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                marginalRate === rate
                  ? 'bg-blue text-white shadow-lg shadow-blue/30'
                  : 'bg-surface-card text-text-secondary hover:bg-surface-hover',
              )}
            >
              {rate}%
              <span className="block text-[9px] font-normal opacity-70">
                {rate === 18 ? 'basic-rate taxpayer' : 'higher / additional rate'}
              </span>
            </button>
          ))}
        </div>
        <p className="text-[10px] text-text-muted mt-2">
          Since 30 October 2024 CGT on shares is 18% (basic) / 24% (higher) — the
          same rates as residential property.
        </p>
      </div>

      {/* Candidates table */}
      {data.candidates.length > 0 ? (
        <section className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <TrendingDown size={14} className="text-rose-light" aria-hidden />
            Harvestable Positions ({data.candidates.length})
          </h2>
          <div className="table-responsive">
            <table className="w-full text-xs table-pro">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Account</th>
                  <th className="!text-right">Cost</th>
                  <th className="!text-right">Value</th>
                  <th className="!text-right">Loss</th>
                  <th className="!text-right">%</th>
                  <th className="!text-right">Tax saved</th>
                  <th>Strategy</th>
                  <th>30-day rule</th>
                </tr>
              </thead>
              <tbody>
                {data.candidates.map(c => (
                  <tr key={c.holding_id}>
                    <td className="text-text-primary font-medium">{c.name}</td>
                    <td className="text-text-muted">{c.account_name}</td>
                    <td className="text-right font-mono text-text-muted">{fmt(c.cost_basis_gbp)}</td>
                    <td className="text-right font-mono text-text-primary">{fmt(c.current_value_gbp)}</td>
                    <td className="text-right"><span className="delta-chip-neg">{fmt(c.unrealised_pnl_gbp)}</span></td>
                    <td className="text-right font-mono text-rose-light">{(c.pct_change ?? 0).toFixed(1)}%</td>
                    <td className="text-right"><span className="delta-chip-pos">{fmt(c.est_tax_saving_gbp)}</span></td>
                    <td>
                      <div className="flex gap-1 flex-wrap">
                        {c.bed_and_isa_suitable && (
                          <span className="badge badge-blue text-[9px]">Bed &amp; ISA</span>
                        )}
                        {c.bed_and_spouse_suitable && (
                          <span className="badge badge-purple text-[9px]">Bed &amp; Spouse</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <WashSaleRiskBadge wsr={c.wash_sale_risk} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <div className="card p-6 text-center">
          <p className="text-sm text-text-muted">
            {data.empty_state_reason === 'all_sheltered'
              ? 'No taxable positions to scan — all holdings are inside ISA / SIPP wrappers where losses have no tax value.'
              : 'No harvestable losses right now — your taxable positions are in profit (or losses are trivial).'}
          </p>
        </div>
      )}

      {/* Recommendations */}
      {data.recommendations.length > 0 && (
        <section>
          <h2 className="panel-title mb-3">Strategies</h2>
          <div className="space-y-3">
            {data.recommendations.map((r, i) => (
              <div
                key={i}
                className={clsx('card p-4 border', PRIORITY_COLOURS[r.priority])}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={clsx(
                    'badge text-[10px]',
                    r.priority === 'HIGH' ? 'badge-red' : r.priority === 'MODERATE' ? 'badge-amber' : 'badge-blue',
                  )}>
                    {r.priority}
                  </span>
                  <p className="text-sm font-semibold text-text-primary">{r.title}</p>
                </div>
                <p className="text-xs text-text-secondary">{r.detail}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Year-End Tax Actions */}
      <YearEndActionsCard />

      {/* Notes */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold flex items-center gap-2">
          <AlertCircle size={13} aria-hidden />
          Rules &amp; notes
        </summary>
        <ul className="mt-3 space-y-1.5 list-disc list-inside">
          {data.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </details>
    </div>
  )
}
